import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FlowguardLogo } from "@/components/FlowguardLogo";
import { PUBLIC_SITE_URL } from "@/utils/publicSite";
import { Loader2, ArrowLeft, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements
} from "@stripe/react-stripe-js";

// Make sure to call loadStripe outside of a component's render to avoid recreating the Stripe object on every render.
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "pk_test_dummy");

const CheckoutForm = ({ clientSecret, pendingId, packageData, name, email }) => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // If we're using the local mock intent
  const isMockIntent = clientSecret && clientSecret.includes("mock");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) {
      return;
    }
    
    setIsLoading(true);

    if (isMockIntent) {
      // Simulate network request for mock fallback
      setTimeout(() => {
        setIsLoading(false);
        navigate(`/register/complete?session_id=checkout_session_local&pending_id=${pendingId}`);
      }, 1500);
      return;
    }

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/register/complete?pending_id=${pendingId}`,
      },
    });

    if (error) {
      if (error.type === "card_error" || error.type === "validation_error") {
        setError(error.message);
      } else {
        setError("An unexpected error occurred.");
      }
      toast.error(error.message || "An error occurred during payment.");
    }
    
    setIsLoading(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <PaymentElement id="payment-element" options={{ layout: "tabs" }} />
      {error && <div className="text-red-500 text-sm mt-2">{error}</div>}
      <button
        disabled={isLoading || !stripe || !elements}
        id="submit"
        className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-slate-900 font-semibold py-4 rounded-md transition-all flex justify-center items-center gap-2 mt-6 disabled:opacity-50"
      >
        {isLoading ? (
          <><Loader2 className="h-5 w-5 animate-spin" /> Processing...</>
        ) : (
          <><ShieldCheck className="h-5 w-5" /> Pay ${(packageData?.amount || 0)}</>
        )}
      </button>
    </form>
  );
};

export default function CheckoutPage() {
  const location = useLocation();
  const navigate = useNavigate();
  
  const { clientSecret, pendingId, package: packageData, name, email } = location.state || {};

  useEffect(() => {
    if (!clientSecret) {
      navigate("/register", { replace: true });
    }
  }, [clientSecret, navigate]);

  if (!clientSecret) {
    return null;
  }

  const appearance = {
    theme: 'night',
    variables: {
      colorPrimary: '#f59e0b', // amber-500
      colorBackground: '#1e293b', // slate-800
      colorText: '#f8fafc',
      colorDanger: '#ef4444',
      fontFamily: 'Manrope, system-ui, sans-serif',
    },
  };
  const options = {
    clientSecret,
    appearance,
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Header/Logo */}
        <a href={PUBLIC_SITE_URL} className="flex items-center justify-center gap-2 mb-8">
          <FlowguardLogo className="h-10 w-10" showText={false} />
          <span className="text-2xl font-bold text-white font-['Manrope']">FlowGuard</span>
        </a>
        
        <div className="bg-slate-800/80 border border-slate-700 rounded-2xl p-6 sm:p-8 shadow-2xl">
          <button 
            type="button"
            onClick={() => navigate(-1)} 
            className="flex items-center text-sm text-slate-400 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </button>
          
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-white mb-2">Complete Payment</h1>
            <p className="text-slate-400 text-sm">
              Subscribe to the <span className="text-amber-500 font-semibold capitalize">{packageData?.tier} Investor</span> plan.
            </p>
          </div>
          
          <div className="bg-slate-900/50 rounded-xl p-4 mb-8 border border-slate-700/50">
            <div className="flex justify-between items-center mb-2">
              <span className="text-slate-400 text-sm">Billed to:</span>
              <span className="text-white font-medium text-sm">{email}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-400 text-sm">Total:</span>
              <span className="text-2xl font-bold text-amber-500">${packageData?.amount}</span>
            </div>
          </div>
          
          {clientSecret && (
            <Elements options={options} stripe={stripePromise}>
              <CheckoutForm 
                clientSecret={clientSecret} 
                pendingId={pendingId} 
                packageData={packageData} 
                name={name} 
                email={email} 
              />
            </Elements>
          )}
        </div>
        
        <p className="text-center text-slate-500 text-xs mt-6">
          Payments are secure and encrypted.
        </p>
      </div>
    </div>
  );
}
